import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import { formatDateTime, toDate } from "../../lib/format";
import { CRM_MEETING_KIND_LABELS, CRM_TASK_TYPE_LABELS } from "../../lib/crm";
import { useAuthStore } from "../../stores/auth";
import { useToastStore } from "../../stores/toast";
import { Button } from "../../components/ui/button";
import { Select } from "../../components/ui/input";
// La route s'exporte elle aussi sous le nom CrmAgenda : on aliase le type de la charge utile.
import type { CrmAgenda as CrmAgendaPayload, Timestamp } from "@shared/types";

type Scope = "all" | "mine";
type Failure = "forbidden" | "error" | null;
type GroupKey = "retard" | "aujourdhui" | "demain" | "semaine" | "plus_tard" | "sans_echeance";

const WINDOWS = [7, 14, 30];

const GROUP_ORDER: GroupKey[] = [
  "retard",
  "aujourdhui",
  "demain",
  "semaine",
  "plus_tard",
  "sans_echeance",
];

const GROUP_LABELS: Record<GroupKey, string> = {
  retard: "En retard",
  aujourdhui: "Aujourd'hui",
  demain: "Demain",
  semaine: "Cette semaine",
  plus_tard: "Plus tard",
  sans_echeance: "Sans échéance",
};

const DAY_MS = 86_400_000;

/** Tâches et rendez-vous sont fondus dans une seule chronologie : même forme pour les deux. */
interface AgendaEntry {
  key: string;
  kind: "task" | "meeting";
  id: string;
  accountId: string;
  accountName: string;
  title: string;
  typeLabel: string;
  when: Timestamp | null;
  at: Date | null;
  assignedUserId: string | null;
  actionable: boolean;
}

export function CrmAgenda() {
  const currentUserId = useAuthStore((s) => s.user?.id ?? null);
  const push = useToastStore((s) => s.push);

  const [scope, setScope] = useState<Scope>("all");
  const [days, setDays] = useState(14);
  const [payload, setPayload] = useState<CrmAgendaPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState<Failure>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setPayload(await api<CrmAgendaPayload>(`/api/crm/agenda?scope=${scope}&days=${days}`));
      setFailure(null);
    } catch (err) {
      // Un agenda vide et un agenda inaccessible disent l'inverse l'un de l'autre :
      // les confondre reviendrait à affirmer qu'aucune relance n'est en retard.
      setPayload(null);
      setFailure(err instanceof ApiError && err.status === 403 ? "forbidden" : "error");
    } finally {
      setLoading(false);
    }
  }, [scope, days]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const entries = useMemo<AgendaEntry[]>(() => {
    if (!payload) return [];
    const tasks: AgendaEntry[] = payload.tasks.map((t) => ({
      key: `task-${t.id}`,
      kind: "task",
      id: t.id,
      accountId: t.accountId,
      accountName: t.accountName,
      title: t.title,
      typeLabel: CRM_TASK_TYPE_LABELS[t.type],
      when: t.dueAt,
      at: toDate(t.dueAt),
      assignedUserId: t.assignedUserId,
      actionable: t.status === "open",
    }));
    const meetings: AgendaEntry[] = payload.meetings.map((m) => ({
      key: `meeting-${m.id}`,
      kind: "meeting",
      id: m.id,
      accountId: m.accountId,
      accountName: m.accountName,
      title: m.title,
      typeLabel: CRM_MEETING_KIND_LABELS[m.kind],
      when: m.startAt,
      at: toDate(m.startAt),
      assignedUserId: m.assignedUserId,
      actionable: m.status === "planifie",
    }));
    return [...tasks, ...meetings].sort(compareEntries);
  }, [payload]);

  const groups = useMemo(() => {
    const now = new Date();
    const buckets = new Map<GroupKey, AgendaEntry[]>();
    for (const entry of entries) {
      const key = bucketOf(entry.at, now);
      const bucket = buckets.get(key);
      if (bucket) bucket.push(entry);
      else buckets.set(key, [entry]);
    }
    return GROUP_ORDER.flatMap((key) => {
      const items = buckets.get(key);
      return items && items.length > 0 ? [{ key, items }] : [];
    });
  }, [entries]);

  const close = async (entry: AgendaEntry) => {
    const collection = entry.kind === "task" ? "tasks" : "meetings";
    const body = entry.kind === "task" ? { status: "done" } : { status: "tenu" };
    setBusyKey(entry.key);
    try {
      await api(`/api/crm/accounts/${entry.accountId}/${collection}/${entry.id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      });
      push(
        "success",
        entry.kind === "task" ? "Suivi terminé" : "Rendez-vous marqué tenu",
        entry.title,
      );
      await refresh();
    } catch {
      push("danger", "Mise à jour impossible", entry.title);
    } finally {
      setBusyKey(null);
    }
  };

  const counts = payload
    ? `${payload.tasks.length} suivi${payload.tasks.length > 1 ? "s" : ""} · ${payload.meetings.length} rendez-vous`
    : "—";

  return (
    <div>
      <header className="page-header">
        <div className="page-header__titles">
          <h1 className="page-header__title">Suivis &amp; rendez-vous</h1>
          <p className="page-header__subtitle">
            Les suivis à faire et les rendez-vous planifiés, regroupés par échéance.
          </p>
        </div>
      </header>

      <div className="crm-toolbar">
        <div className="crm-toolbar__filters">
          <div className="crm-toolbar__views">
            <button
              type="button"
              className={`crm-toolbar__view${scope === "all" ? " is-active" : ""}`}
              onClick={() => setScope("all")}
            >
              Tous
            </button>
            <button
              type="button"
              className={`crm-toolbar__view${scope === "mine" ? " is-active" : ""}`}
              onClick={() => setScope("mine")}
            >
              Les miens
            </button>
          </div>
          <Select
            aria-label="Fenêtre"
            value={String(days)}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            {WINDOWS.map((w) => (
              <option key={w} value={w}>
                {w} jours
              </option>
            ))}
          </Select>
        </div>
        <div className="crm-toolbar__spacer" />
        <span className="crm-chip crm-chip--muted">{counts}</span>
      </div>

      {loading && !payload ? (
        <p className="crm-empty">Chargement de l'agenda…</p>
      ) : failure === "forbidden" ? (
        <p className="crm-empty">
          Le CRM est réservé aux administrateurs. Demande un accès admin pour consulter les suivis.
        </p>
      ) : failure === "error" ? (
        <p className="crm-empty">Agenda indisponible — réessayez dans un instant.</p>
      ) : groups.length === 0 ? (
        <p className="crm-empty">
          Rien de prévu sur cette fenêtre. Les suivis et rendez-vous s'ajoutent depuis une fiche
          dossier.
        </p>
      ) : (
        <div className="crm-agenda">
          {groups.map((group) => (
            <section className="crm-agenda__group" key={group.key}>
              <h2 className="crm-agenda__group-title">
                {GROUP_LABELS[group.key]} · {group.items.length}
              </h2>
              {group.items.map((entry) => (
                <div
                  key={entry.key}
                  className={`crm-agenda__item${group.key === "retard" ? " crm-agenda__item--overdue" : ""}`}
                >
                  <span className="crm-agenda__when">{whenLabel(entry.when)}</span>
                  <span className="crm-agenda__what">
                    <span
                      className={entry.kind === "meeting" ? "crm-chip" : "crm-chip crm-chip--muted"}
                    >
                      {entry.kind === "meeting" ? "RDV" : "Suivi"}
                    </span>
                    {entry.title}
                    <span className="crm-chip crm-chip--muted">{entry.typeLabel}</span>
                    <Link className="crm-linkline" to={`/crm/dossiers/${entry.accountId}`}>
                      {entry.accountName}
                    </Link>
                  </span>
                  <span className="crm-agenda__who">
                    {assigneeLabel(entry.assignedUserId, currentUserId)}
                  </span>
                  {/* La colonne d'action existe même vide : les rangées non
                      actionnables doivent garder le même gabarit. */}
                  <span className="crm-agenda__action">
                    {entry.actionable ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busyKey === entry.key}
                        onClick={() => void close(entry)}
                      >
                        {entry.kind === "meeting" ? "Marquer tenu" : "Terminer"}
                      </Button>
                    ) : null}
                  </span>
                </div>
              ))}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

/** Les entrées sans échéance ferment la marche, quel que soit leur type. */
function compareEntries(a: AgendaEntry, b: AgendaEntry): number {
  if (a.at && b.at) {
    return a.at.getTime() - b.at.getTime() || a.title.localeCompare(b.title, "fr-CA");
  }
  if (a.at) return -1;
  if (b.at) return 1;
  return a.title.localeCompare(b.title, "fr-CA");
}

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function bucketOf(at: Date | null, now: Date): GroupKey {
  if (!at) return "sans_echeance";
  // Un rendez-vous commencé mais jamais clôturé mérite le même rappel qu'une tâche échue.
  if (at.getTime() < now.getTime()) return "retard";
  const days = Math.round((startOfDay(at) - startOfDay(now)) / DAY_MS);
  if (days <= 0) return "aujourdhui";
  if (days === 1) return "demain";
  if (days <= 7) return "semaine";
  return "plus_tard";
}

function whenLabel(when: Timestamp | null): string {
  if (when === null) return "—";
  return formatDateTime(when, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** L'agenda ne transporte que l'identifiant de l'assigné : on n'affiche que ce qui est vérifiable. */
function assigneeLabel(assignedUserId: string | null, currentUserId: string | null): string {
  if (!assignedUserId) return "Non assigné";
  return assignedUserId === currentUserId ? "Moi" : "Autre responsable";
}
